import api, { sharedApiGet } from '../services/api';
import { adminAPI, clientAPI } from '../services/api';
import { clearActorAvatarCaches, primeActorAvatarRegistry, normalizeActorRole } from './actorAvatarCache';

/** Aligné planning — un seul GET clients partagé (sharedApiGet). */
const PLANNING_CLIENTS_PARAMS = { limit: 10000, fields: 'planning' };

let profilesPrimed = false;
let profilesPrimePromise = null;
let primedViewerKey = null;

export function buildNotificationAvatarViewer(userData) {
  if (!userData || userData.id == null) return null;
  return {
    id: userData.id,
    role: String(userData.role || '').trim().toLowerCase(),
    avatar_url: userData.avatar_url || null
  };
}

function extractListPayload(res, collectionKey) {
  const d = res?.data;
  if (!d) return [];
  if (Array.isArray(d.data)) return d.data;
  if (collectionKey && Array.isArray(d[collectionKey])) return d[collectionKey];
  if (Array.isArray(d)) return d;
  return [];
}

function mapClientRow(c) {
  if (!c || c.id == null) return null;
  return {
    id: c.id,
    role: 'client',
    name: c.nom || c.name || c.code_client,
    email: c.mail || c.email,
    avatar_url: c.avatar_url || null
  };
}

function mapIntervenantRow(iv) {
  if (!iv || iv.id == null) return null;
  return {
    id: iv.id,
    role: 'intervenant',
    name: iv.nom_complet || iv.name,
    email: iv.email,
    avatar_url: iv.avatar_url || null,
    code_couleur: iv.code_couleur || null
  };
}

function mapAdminRow(a) {
  if (!a || a.id == null) return null;
  return {
    id: a.id,
    role: a.role || 'admin',
    name: a.name,
    email: a.email,
    avatar_url: a.avatar_url || null
  };
}

async function primeClientViewerEntries(entries) {
  try {
    const res = await clientAPI.getMyProfile();
    const client = res?.data?.client || res?.data;
    const m = mapClientRow(client);
    if (m) entries.push(m);
  } catch {
    /* non bloquant */
  }
  try {
    const resIv = await api.get('/client/interventions');
    const list = extractListPayload(resIv, 'interventions');
    const seenIv = new Set();
    list.forEach((row) => {
      if (row?.intervenant_id == null || seenIv.has(String(row.intervenant_id))) return;
      seenIv.add(String(row.intervenant_id));
      const mIv = mapIntervenantRow({
        id: row.intervenant_id,
        name: row.intervenant || row.intervenant_nom_affiche,
        avatar_url: row.intervenant_avatar_url,
        code_couleur: row.intervenant_couleur || row.intervenant_code_couleur
      });
      if (mIv) entries.push(mIv);
    });
  } catch {
    /* non bloquant */
  }
}

async function primeIntervenantViewerEntries(entries) {
  try {
    const res = await api.get('/intervenant/profil');
    const iv = res?.data?.intervenant || res?.data;
    const m = mapIntervenantRow(iv);
    if (m) entries.push(m);
  } catch {
    /* non bloquant */
  }
  try {
    const resCl = await api.get('/intervenant/interventions');
    const list = extractListPayload(resCl, 'interventions');
    const seenCl = new Set();
    list.forEach((row) => {
      if (row?.client_id == null || seenCl.has(String(row.client_id))) return;
      seenCl.add(String(row.client_id));
      const mCl = mapClientRow({
        id: row.client_id,
        nom: row.client_nom || row.client_nom_complet || row.client,
        avatar_url: row.client_avatar_url,
        code_couleur: row.client_code_couleur || row.client_couleur
      });
      if (mCl) entries.push(mCl);
    });
  } catch {
    /* non bloquant */
  }
}

/**
 * Charge les profils pour alimenter le cache avatars (clé = rôle + id viewer).
 * Sans viewer : no-op (évite profilesPrimed bloqué par un appel admin sans contexte).
 */
export function ensureActorAvatarProfilesPrimed(viewer = null) {
  if (viewer?.id == null) {
    return Promise.resolve();
  }

  const viewerKey = `${normalizeActorRole(viewer.role)}:${viewer.id}`;
  if (profilesPrimed && primedViewerKey === viewerKey) {
    return profilesPrimePromise || Promise.resolve();
  }
  if (profilesPrimed && primedViewerKey !== viewerKey) {
    resetActorAvatarProfilesPrime();
  }
  if (profilesPrimePromise) return profilesPrimePromise;

  const viewerRole = normalizeActorRole(viewer.role);

  profilesPrimePromise = (async () => {
    const entries = [];

    if (viewerRole === 'client') {
      await primeClientViewerEntries(entries);
      primeActorAvatarRegistry(entries);
      profilesPrimed = true;
      primedViewerKey = viewerKey;
      return;
    }

    if (viewerRole === 'intervenant') {
      await primeIntervenantViewerEntries(entries);
      primeActorAvatarRegistry(entries);
      profilesPrimed = true;
      primedViewerKey = viewerKey;
      return;
    }

    // Staff (admin / superadmin) : listes IV + clients.
    // getAdmins() = route SuperAdmin uniquement → ne pas l'appeler en Admin (403 console).
    const adminListPromise = viewerRole === 'superadmin'
      ? adminAPI.getAdmins().then((r) => (Array.isArray(r.data) ? r.data : []))
      : Promise.resolve([]);

    if (viewerRole === 'admin' || viewerRole === 'superadmin') {
      const self = mapAdminRow({
        id: viewer.id,
        role: viewerRole,
        name: viewer.name,
        email: viewer.email,
        avatar_url: viewer.avatar_url || null
      });
      if (self) entries.push(self);
    }

    const batches = await Promise.allSettled([
      adminListPromise,
      sharedApiGet('/auth/intervenants').then((r) => {
        const list = r?.data?.intervenants || r?.data;
        return Array.isArray(list) ? list : [];
      }),
      sharedApiGet('/clients', { params: PLANNING_CLIENTS_PARAMS }).then((r) => {
        const list = r?.data?.data || r?.data?.clients || r?.data;
        return Array.isArray(list) ? list : [];
      })
    ]);

    if (batches[0].status === 'fulfilled') {
      batches[0].value.forEach((row) => {
        const m = mapAdminRow(row);
        if (m) entries.push(m);
      });
    }
    if (batches[1].status === 'fulfilled') {
      batches[1].value.forEach((row) => {
        const m = mapIntervenantRow(row);
        if (m) entries.push(m);
      });
    }
    if (batches[2].status === 'fulfilled') {
      batches[2].value.forEach((row) => {
        const m = mapClientRow(row);
        if (m) entries.push(m);
      });
    }

    primeActorAvatarRegistry(entries);
    profilesPrimed = true;
    primedViewerKey = viewerKey;
  })().catch(() => {
    profilesPrimed = true;
    primedViewerKey = viewerKey;
  });

  return profilesPrimePromise;
}

export function resetActorAvatarProfilesPrime() {
  profilesPrimed = false;
  profilesPrimePromise = null;
  primedViewerKey = null;
  clearActorAvatarCaches();
}
